import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { integrations } from './marketplaceData';
import { Integration } from './types';

const CatalogView = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCapability, setSelectedCapability] = useState<string>('All');
  const [selectedVendor, setSelectedVendor] = useState<string>('All');
  const [selectedCategory, setSelectedCategory] = useState<string>('All');

  // Extract unique values for filters
  const capabilities = ['All', 'Partner Hosted', 'Log Ingestion', 'Enrichment', 'Automation'];
  const vendors = ['All', ...Array.from(new Set(integrations.map((i) => i.vendor)))];
  const categories = ['All', ...Array.from(new Set(integrations.map((i) => i.category)))];

  // Filter integrations
  const filteredIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      const matchesSearch =
        integration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        integration.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        integration.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCapability =
        selectedCapability === 'All' ||
        (selectedCapability === 'Partner Hosted' && integration.partnerHosted) ||
        (selectedCapability === 'Log Ingestion' && integration.logIngestion) ||
        (selectedCapability === 'Enrichment' && integration.enrichment) ||
        (selectedCapability === 'Automation' && integration.automation);

      const matchesVendor = selectedVendor === 'All' || integration.vendor === selectedVendor;
      const matchesCategory = selectedCategory === 'All' || integration.category === selectedCategory;

      return matchesSearch && matchesCapability && matchesVendor && matchesCategory;
    });
  }, [searchTerm, selectedCapability, selectedVendor, selectedCategory]);

  return (
    <div className="p-4 sm:p-6 min-h-screen">
      {/* Search and Filters */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4 mb-6">
        {/* Search */}
        <div className="relative w-full lg:flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search integrations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[#1f1f1f] border border-[#474646] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-[#D12929]"
          />
        </div>

        {/* Capability Filter */}
        <select
          value={selectedCapability}
          onChange={(e) => setSelectedCapability(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 bg-[#1f1f1f] border border-[#474646] rounded-lg text-white cursor-pointer focus:outline-none focus:border-[#D12929]"
        >
          <option value="All">Capabilities ▼</option>
          {capabilities.map((cap) => (
            <option key={cap} value={cap}>
              {cap}
            </option>
          ))}
        </select>

        {/* Vendor Filter */}
        <select
          value={selectedVendor}
          onChange={(e) => setSelectedVendor(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 bg-[#1f1f1f] border border-[#474646] rounded-lg text-white cursor-pointer focus:outline-none focus:border-[#D12929]"
        >
          <option value="All">Vendors ▼</option>
          {vendors.map((vendor) => (
            <option key={vendor} value={vendor}>
              {vendor}
            </option>
          ))}
        </select>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="w-full sm:w-auto px-4 py-2 bg-[#1f1f1f] border border-[#474646] rounded-lg text-white cursor-pointer focus:outline-none focus:border-[#D12929]"
        >
          <option value="All">Categories ▼</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Catalog Count */}
      <h2 className="text-white text-xl font-semibold mb-6">Catalog ({filteredIntegrations.length})</h2>

      {/* Grid of Integration Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredIntegrations.map((integration) => (
          <div
            key={integration.id}
            onClick={() => !integration.comingSoon && navigate(`/marketplace/integration/${integration.id}`)}
            className={`bg-[#1f1f1f] border border-[#474646] rounded-lg p-4 transition-colors relative ${
              integration.comingSoon ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-[#D12929]'
            }`}
          >
            {/* Coming Soon overlay badge */}
            {integration.comingSoon && (
              <div className="absolute top-3 right-3 z-10">
                <span className="px-2 py-1 bg-[#474646] text-gray-300 text-xs font-semibold rounded-full border border-[#606060]">
                  Coming Soon
                </span>
              </div>
            )}

            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
              {/* Logo */}
              <div className="w-12 h-12 bg-[#2e2e2f] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {integration.logoUrl ? (
                  <img
                    src={integration.logoUrl}
                    alt={integration.name}
                    className="w-10 h-10 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling!.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <span className={`text-white font-bold text-sm ${integration.logoUrl ? 'hidden' : ''}`}>{integration.icon}</span>
              </div>

              {/* Name and Vendor */}
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm mb-1 truncate pr-6">{integration.name}</h3>
                <p className="text-gray-400 text-xs truncate">{integration.vendor}</p>
              </div>
            </div>

            {/* Category badge (below header when no coming-soon) */}
            {!integration.comingSoon && (
              <div className="mb-2">
                <span className="px-2 py-1 bg-[#D12929] text-white text-xs rounded whitespace-nowrap">{integration.category}</span>
              </div>
            )}
            {integration.comingSoon && (
              <div className="mb-2">
                <span className="px-2 py-1 bg-[#2e2e2f] text-gray-400 text-xs rounded whitespace-nowrap border border-[#474646]">
                  {integration.category}
                </span>
              </div>
            )}

            {/* Description */}
            <p className="text-gray-300 text-sm mb-3 line-clamp-3">{integration.description}</p>

            {/* Capabilities */}
            <div className="flex flex-wrap gap-2">
              {integration.partnerHosted && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <span>🤝</span>
                  <span>Partner Hosted</span>
                </div>
              )}
              {integration.logIngestion && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <span>📊</span>
                  <span>Log Ingestion</span>
                </div>
              )}
              {integration.enrichment && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <span>✨</span>
                  <span>Enrichment</span>
                </div>
              )}
              {integration.automation && (
                <div className="flex items-center gap-1 text-xs text-gray-400">
                  <span>⚡</span>
                  <span>Automation</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredIntegrations.length === 0 && (
        <div className="text-center text-gray-400 py-12">
          <p className="text-lg">No integrations found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CatalogView;
